import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, SETUP_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { placeNameConflict, problemFieldErrors, problemMessage } from "@/api/client";
import { usePlaces } from "@/hooks/usePlaces";
import type { Place, PlaceKind } from "@/api/types";
import { DialogShell } from "@/components/DialogShell";
import "./Places.css";

/** Mirrors `PlaceEndpoints.AllowedKinds` — option VALUES are the lowercase strings the
 *  API's CHECK constraint and `PlaceKind` (api/types.ts) both use; labels are what a
 *  farmer reads. */
const KIND_OPTIONS: { value: PlaceKind; label: string }[] = [
  { value: "field", label: "Field" },
  { value: "yard", label: "Yard" },
  { value: "shop", label: "Shop" },
  { value: "house", label: "House" },
  { value: "other", label: "Other" },
];

function kindLabel(kind: PlaceKind): string {
  return KIND_OPTIONS.find((k) => k.value === kind)?.label ?? kind;
}

/** Matches `PlaceEndpoints.DefaultRadiusYards` — the server's own default, restated here
 *  only so the form has something to show before anything is typed; the server remains
 *  the actual default when `radiusYards` is omitted from the request entirely. */
const DEFAULT_RADIUS_YARDS = 440;
const YARDS_PER_MILE = 1760;

/**
 * "440" -> "¼ mile", and similar for the other common fractions a farmer would actually
 * pick. Presentation only — a decimal fallback covers anything else, and the yards value
 * actually submitted is always the one typed, never rounded to match this label.
 */
function radiusLabel(yards: number): string {
  const FRACTIONS: Record<number, string> = {
    220: "⅛ mile",
    440: "¼ mile",
    880: "½ mile",
    1320: "¾ mile",
    1760: "1 mile",
  };
  if (FRACTIONS[yards]) return FRACTIONS[yards];
  if (!Number.isFinite(yards) || yards <= 0) return "";
  const miles = yards / YARDS_PER_MILE;
  return `${miles < 1 ? miles.toFixed(2) : miles.toFixed(1)} mi`;
}

/** Every numeric value held as a STRING until submit — same convention PaidLeave.tsx's
 *  `ProgramForm` uses, and for the same reason: an `<input>` only ever has a string, and
 *  parsing on every keystroke would either fight the browser's own numeric editing or
 *  quietly coerce "" to 0 long before the submit-time guard below gets a chance to catch
 *  it (@ui-check below). */
interface PlaceFormState {
  name: string;
  kind: PlaceKind;
  lat: string;
  lon: string;
  radiusYards: string;
}

function blankForm(): PlaceFormState {
  return { name: "", kind: "field", lat: "", lon: "", radiusYards: String(DEFAULT_RADIUS_YARDS) };
}

function formFromPlace(place: Place): PlaceFormState {
  return {
    name: place.name,
    kind: place.kind,
    lat: String(place.lat),
    lon: String(place.lon),
    radiusYards: String(place.radiusYards),
  };
}

/**
 * ef-021 — the farmer names their own ground. `Payroll.tsx`'s "More payroll screens" nav
 * links here, same as Year-end/H-2A/Approved hours; this is a payroll sub-screen, not a
 * fifth nav destination (see App.tsx's routing note).
 *
 * WHY A SINGLE SHARED FORM FOR ADD AND EDIT, rather than a second form or a modal.
 * `PaidLeave.tsx`'s override form is the same shape: one set of fields, re-seeded from
 * whichever record is being worked on, with the submit label naming the mode
 * ("Save place" vs "Save changes"). A farmer editing the Shop does not need a different
 * screen from the one that created it.
 */
export function Places() {
  const { active, archived, status, error, create, update, archive, restore } = usePlaces();

  const [form, setForm] = useState<PlaceFormState>(blankForm());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [locationHint, setLocationHint] = useState<string | null>(null);
  /** ef-038-3 — set when the create above 409'd against an ARCHIVED place (never an
   *  active one): the id to offer "Restore it instead" for, alongside the flat failure
   *  message `formError` already shows. */
  const [conflictPlaceId, setConflictPlaceId] = useState<string | null>(null);
  const [restoringConflict, setRestoringConflict] = useState(false);

  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archiving, setArchiving] = useState<Place | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  /** ef-038-3 — Restore, straight from the archived disclosure's own row (no confirm
   *  dialog: unlike archiving, bringing a place back destroys nothing). */
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const nameId = useId();
  const kindId = useId();
  const latId = useId();
  const lonId = useId();
  const radiusId = useId();

  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const archiveTriggerRef = useRef<HTMLButtonElement | null>(null);

  function startEdit(place: Place): void {
    setEditingId(place.id);
    setForm(formFromPlace(place));
    setFormError(null);
    setFieldErrors(null);
    setLocationHint(null);
    setConflictPlaceId(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setForm(blankForm());
    setFormError(null);
    setFieldErrors(null);
    setConflictPlaceId(null);
  }

  /**
   * `navigator.geolocation`, with a graceful hand-off rather than a dead end. Denied,
   * unavailable, and timed-out all land in the SAME explanation — the farmer's next step
   * (type it in) is identical either way, and distinguishing them would be detail nobody
   * acts on differently.
   *
   * @ui-check: the fix lands in the SAME two `<input>`s typing would use (latField/
   * lonField), never a hidden field — a screen reader or a canary reading only the
   * visible form must see the exact value that will be submitted.
   */
  function useMyLocation(): void {
    setLocationHint(null);
    if (!("geolocation" in navigator)) {
      setLocationHint("This browser can't share your location — type the coordinates in by hand.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((f) => ({
          ...f,
          lat: String(position.coords.latitude),
          lon: String(position.coords.longitude),
        }));
        setLocationHint(null);
      },
      () => {
        setLocationHint("We couldn't get your location — type the coordinates in by hand.");
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);
    setConflictPlaceId(null);

    const trimmedName = form.name.trim();
    const lat = form.lat.trim() === "" ? NaN : Number(form.lat);
    const lon = form.lon.trim() === "" ? NaN : Number(form.lon);
    const radiusInput = form.radiusYards.trim() === "" ? NaN : Number(form.radiusYards);

    /*
     * @ui-check: client-side ONLY — the server's own field-by-field checks
     * (PlaceEndpoints.ValidateFields: name length, kind, lat/lon range, radius bounds)
     * still run after this and remain the actual contract enforcement. This guard exists
     * for the one case a missing value would otherwise silently become a REAL coordinate
     * rather than a validation error: `Number("")` is `0`, not `NaN`, so a farmer who
     * declines geolocation and types nothing must never reach the network at all — a
     * place at 0,0 the server would happily accept is worse than a client refusing to ask.
     */
    const missing: Record<string, string[]> = {};
    if (trimmedName === "") missing.name = ["A name is required."];
    if (Number.isNaN(lat)) missing.lat = ["Enter a latitude, or use your device's location."];
    if (Number.isNaN(lon)) missing.lon = ["Enter a longitude, or use your device's location."];
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    const radiusYards = Number.isNaN(radiusInput) ? undefined : radiusInput;

    setSaving(true);
    try {
      if (editingId) {
        await update(editingId, { name: trimmedName, kind: form.kind, lat, lon, radiusYards });
      } else {
        await create({ name: trimmedName, kind: form.kind, lat, lon, radiusYards });
      }
      cancelEdit();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this place."));
      // ef-038-3 — a 409 against an ARCHIVED place offers Restore instead of leaving the
      // farmer to retype everything under a different name. An active-place clash still
      // gets only the flat failure above (placeNameConflict returns archived: false).
      const conflict = placeNameConflict(err);
      setConflictPlaceId(conflict?.archived && conflict.placeId ? conflict.placeId : null);
    } finally {
      setSaving(false);
    }
  }

  /** The "Restore it instead" offer on a create that 409'd against an archived place. */
  async function restoreConflicting(): Promise<void> {
    if (!conflictPlaceId) return;
    setRestoringConflict(true);
    try {
      await restore(conflictPlaceId);
      cancelEdit();
    } catch (err) {
      setFormError(problemMessage(err, "Could not restore this place."));
    } finally {
      setRestoringConflict(false);
    }
  }

  /** Restore straight from the archived disclosure's own row. */
  async function handleRestore(place: Place): Promise<void> {
    setRestoringId(place.id);
    setRestoreError(null);
    try {
      await restore(place.id);
    } catch (err) {
      setRestoreError(problemMessage(err, "Could not restore this place."));
    } finally {
      setRestoringId(null);
    }
  }

  function openArchiveConfirm(place: Place, trigger: HTMLButtonElement | null): void {
    archiveTriggerRef.current = trigger;
    setArchiving(place);
    setArchiveError(null);
  }

  function closeArchiveConfirm(): void {
    setArchiving(null);
    setArchiveError(null);
    archiveTriggerRef.current?.focus();
  }

  async function confirmArchive(): Promise<void> {
    if (!archiving) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const archivedId = archiving.id;
      await archive(archivedId);
      setArchiving(null);
      // Editing the very row being archived would submit an update against a place that
      // no longer exists active — leave the shared form in add mode instead.
      if (editingId === archivedId) cancelEdit();
    } catch (err) {
      setArchiveError(problemMessage(err, "Could not archive this place."));
    } finally {
      setArchiveBusy(false);
    }
  }

  const activeCount = active?.length ?? 0;
  const coordError = fieldErrors?.lat?.[0] ?? fieldErrors?.lon?.[0];

  return (
    <main className="places-page">
      {/* The back link is gone (ADR 0009 §2: "No back buttons") — Saved places is now a
          Setup tab, reached and left via the area tab strip below rather than a bespoke
          button. */}
      <PageHeader
        title="Saved places"
        subtitle="Name the ground your team clocks in and out of, so a punch reads as a place, not a coordinate."
      />

      <AreaTabs area="/setup" tabs={SETUP_TABS} label="Setup" />

      <div className="places-page__grid">
        <section className="places-page__form-card" aria-labelledby="places-form-heading">
          <h2 id="places-form-heading" className="places-page__card-title">
            {editingId ? "Edit place" : "Add a place"}
          </h2>

          <form className="places-page__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="places-page__field">
              <label htmlFor={nameId}>Name</label>
              <input
                id={nameId}
                type="text"
                value={form.name}
                maxLength={80}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-invalid={fieldErrors?.name ? true : undefined}
              />
              {fieldErrors?.name && (
                <p className="places-page__field-error" role="alert">
                  {fieldErrors.name[0]}
                </p>
              )}
            </div>

            <div className="places-page__field">
              <label htmlFor={kindId}>Kind</label>
              <select
                id={kindId}
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as PlaceKind })}
              >
                {KIND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="places-page__coords">
              <div className="places-page__field">
                <label htmlFor={latId}>Latitude</label>
                <input
                  id={latId}
                  type="text"
                  inputMode="decimal"
                  value={form.lat}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })}
                  aria-invalid={fieldErrors?.lat ? true : undefined}
                />
              </div>
              <div className="places-page__field">
                <label htmlFor={lonId}>Longitude</label>
                <input
                  id={lonId}
                  type="text"
                  inputMode="decimal"
                  value={form.lon}
                  onChange={(e) => setForm({ ...form, lon: e.target.value })}
                  aria-invalid={fieldErrors?.lon ? true : undefined}
                />
              </div>
            </div>
            {coordError && (
              <p className="places-page__field-error" role="alert">
                {coordError}
              </p>
            )}

            <button
              type="button"
              className="btn btn--ghost places-page__geo"
              onClick={useMyLocation}
            >
              Use my location
            </button>

            {/* role="status", never "alert" — a declined/unavailable fix is an ordinary
                thing (see PunchLocationLine.tsx's own reasoning for the same
                distinction), not a failure this form should read as one. */}
            {locationHint && (
              <p className="places-page__hint" role="status">
                {locationHint}
              </p>
            )}

            <div className="places-page__field">
              <label htmlFor={radiusId}>Radius (yards)</label>
              <input
                id={radiusId}
                type="number"
                min={20}
                max={3520}
                step={1}
                value={form.radiusYards}
                onChange={(e) => setForm({ ...form, radiusYards: e.target.value })}
                aria-invalid={fieldErrors?.radiusYards ? true : undefined}
              />
              <p className="places-page__radius-hint">
                {radiusLabel(Number(form.radiusYards))} radius — how close a punch has to
                land to count as here.
              </p>
              {fieldErrors?.radiusYards && (
                <p className="places-page__field-error" role="alert">
                  {fieldErrors.radiusYards[0]}
                </p>
              )}
            </div>

            {/* The general refusal — a 409 name conflict, or any server rejection that
                is not about one specific field above. Suppressed once field-level errors
                are showing, so a farmer never reads the same rejection twice. */}
            {formError && !fieldErrors && (
              <p className="places-page__form-error" role="alert">
                {formError}
              </p>
            )}

            {conflictPlaceId && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void restoreConflicting()}
                disabled={restoringConflict}
              >
                {restoringConflict ? "Restoring…" : "Restore it instead"}
              </button>
            )}

            <div className="places-page__form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? "Saving…" : editingId ? "Save changes" : "Save place"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        {/* data-state is the tester's ground truth for this section (ui-breadcrumbs
            §1): loading / error / empty / ready, read off the DOM rather than inferred
            from whether some sentence happens to be on screen. */}
        <section
          className="places-page__list-card"
          aria-labelledby="places-heading"
          data-state={status === "ready" && activeCount === 0 ? "empty" : status}
        >
          <h2 id="places-heading" className="places-page__card-title">
            Your saved places
          </h2>

          {status === "loading" && <StateMessage tone="loading" title="Loading saved places…" />}

          {status === "error" && (
            <StateMessage
              tone="error"
              title="Could not load saved places."
              detail={error ?? undefined}
            />
          )}

          {status === "ready" && activeCount === 0 && (
            <StateMessage
              tone="empty"
              title="No saved places yet."
              detail="Add the fields, yards and buildings your team clocks in from, and a punch will read as a place instead of a bare coordinate."
            />
          )}

          {status === "ready" && active !== null && activeCount > 0 && (
            <ul className="places-page__list" aria-labelledby="places-heading">
              {active.map((place) => (
                <li key={place.id} className="places-page__row">
                  <span className="places-page__meta">
                    <span className="places-page__name">{place.name}</span>
                    <span className="places-page__kind">
                      {kindLabel(place.kind)} · {radiusLabel(place.radiusYards)} radius
                    </span>
                  </span>
                  <span className="places-page__row-actions">
                    <button type="button" className="btn btn--ghost" onClick={() => startEdit(place)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={(e) => openArchiveConfirm(place, e.currentTarget)}
                    >
                      Archive
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="btn btn--ghost places-page__archived-toggle"
            aria-expanded={archivedOpen}
            onClick={() => setArchivedOpen((v) => !v)}
          >
            {archivedOpen ? "Hide" : "Show"} archived places
            {archived.length > 0 ? ` (${archived.length})` : ""}
          </button>

          {/* A <fieldset>/<legend> pair, not a styled <div> — the legend gives the
              disclosure a real accessible NAME ("Archived places") the way a
              <section aria-labelledby> gives one to the active list above, without a
              second visible heading repeating the toggle button's own label. */}
          {archivedOpen && (
            <fieldset className="places-page__archived">
              <legend className="visually-hidden">Archived places</legend>
              {restoreError && (
                <p className="places-page__field-error" role="alert">
                  {restoreError}
                </p>
              )}
              {archived.length === 0 ? (
                <p className="places-page__archived-empty">Nothing archived yet.</p>
              ) : (
                <ul className="places-page__list places-page__list--archived">
                  {archived.map((place) => (
                    <li key={place.id} className="places-page__row places-page__row--archived">
                      <span className="places-page__meta">
                        <span className="places-page__name">{place.name}</span>
                        <span className="places-page__kind">{kindLabel(place.kind)}</span>
                      </span>
                      <span className="places-page__row-actions">
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => void handleRestore(place)}
                          disabled={restoringId === place.id}
                        >
                          {restoringId === place.id ? "Restoring…" : "Restore"}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          )}
        </section>
      </div>

      {archiving && (
        <DialogShell
          title={<>Archive {archiving.name}?</>}
          titleId="archive-place-title"
          role="alertdialog"
          size="sm"
          onClose={closeArchiveConfirm}
          busy={archiveBusy}
          initialFocus={cancelBtnRef}
          describedBy="archive-place-desc"
          footer={
            <>
              <button
                ref={cancelBtnRef}
                type="button"
                className="btn btn--ghost"
                onClick={closeArchiveConfirm}
                disabled={archiveBusy}
              >
                Cancel
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                className="btn btn--danger"
                onClick={() => void confirmArchive()}
                disabled={archiveBusy}
              >
                {archiveBusy ? "Archiving…" : "Archive"}
              </button>
            </>
          }
        >
          <p id="archive-place-desc" className="places-page__dialog-desc">
            Every punch recorded here — past and future — will read as a distance from
            the nearest saved place instead of {archiving.name}, or as an unsaved
            location when none is close enough to measure against. Names are resolved
            live, never stored on the punch, so nothing is rewritten and nothing is
            lost. You can restore {archiving.name} later from the archived list below,
            if you change your mind.
          </p>
          {archiveError && (
            <p className="places-page__dialog-error" role="alert">
              {archiveError}
            </p>
          )}
        </DialogShell>
      )}
    </main>
  );
}
